// src/pages/CourseTime/components/DiscussionModal.comp.jsx
import React, { useEffect, useState } from 'react';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  TabsContainer,
  Tab,
  ContentArea,
  SlideTitle,
  SlideDescription,
  TabContent,
  MessagesContainer,
  MessageItem,
  MessageHeader,
  UserAvatar,
  UserName,
  MessageTime,
  MessageContent,
  MessageActions,
  ActionButton,
  EditModeContainer,
  EditInput,
  EditActions,
  EditButton,
  EditedIndicator,
  ConfirmationOverlay,
  ConfirmationModal,
  ConfirmationHeader,
  ConfirmationIcon,
  ConfirmationTitle,
  ConfirmationMessage,
  ConfirmationActions,
  ConfirmationButton,
  InputArea,
  InputContainer,
  MessageInput,
  SendButton,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateSubtext,
  ErrorContainer,
  ErrorText,
  CharacterCounter, EditMessageContent
} from '../styles/discussion.styles';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

// Icon components
const CloseIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const SendIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    style={{ transform: "rotate(90deg)" }}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
    />
  </svg>
);

const ChatBubbleIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
    />
  </svg>
);

const EditIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

const WarningIcon = () => (
  <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
    />
  </svg>
);

const DiscussionModal = ({
  isOpen,
  onClose,
  messages,
  loading,
  error,
  newMessage,
  onMessageChange,
  onKeyPress,
  onSendMessage,
  posting,
  canSend,
  characterCount,
  isOverLimit,
  maxLength,
  formatMessageTime,
  getUserInitials,
  messagesEndRef,
  inputRef,
  onRefresh,
  courseTitle = "Course Discussion",
  themeColor,
  currentProfile,
  // New props for tab support
  discussionSessions = [],
  activeTabIndex = 0,
  activeSession = null,
  onTabSwitch,
  // New props for edit/delete functionality
  onUpdateMessage,
  onDeleteMessage,
  pageLc
}) => {
  // State for edit mode
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showDeleteConfirm) {
          cancelDelete();
        } else if (isOpen) {
          onClose();
        }
      }
    };

    if (isOpen || showDeleteConfirm) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showDeleteConfirm, onClose]);

  // Handle overlay click to close modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Helper function to check if message is from current user
  const isCurrentUserMessage = (message) => {
    return currentProfile && message.user && message.user.id === currentProfile._id;
  };

  // Edit message handlers
  const startEdit = (message) => {
    setEditingMessageId(message.id);
    setEditText(message.message);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditText('');
    setUpdating(false);
  };

  const saveEdit = async () => {
    if (!editText.trim() || updating) return;
    
    setUpdating(true);
    try {
      await onUpdateMessage(editingMessageId, editText.trim());
      setEditingMessageId(null);
      setEditText('');
    } catch (error) {
      // Error handling is done in the hook
      console.error('Failed to update message:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Delete message handlers
  const showDeleteConfirmation = (message) => {
    setMessageToDelete(message);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setMessageToDelete(null);
    setDeleting(false);
  };

  const confirmDelete = async () => {
    if (!messageToDelete || deleting) return;
    
    setDeleting(true);
    try {
      await onDeleteMessage(messageToDelete.id);
      setShowDeleteConfirm(false);
      setMessageToDelete(null);
    } catch (error) {
      // Error handling is done in the hook
      console.error('Failed to delete message:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Handle edit input key press
  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        {/* Header */}
        <ModalHeader>
          <ModalTitle themeColor={themeColor}>{courseTitle}</ModalTitle>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <CloseButton
              onClick={onRefresh}
              title={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__refresh_messages')}
              disabled={loading}
            >
              <RefreshIcon />
            </CloseButton>
            <CloseButton onClick={onClose} title={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__close_discussion')}>
              <CloseIcon />
            </CloseButton>
          </div>
        </ModalHeader>

        {/* Tabs */}
        {discussionSessions.length > 0 && (
          <TabsContainer>
            {discussionSessions.map((session, index) => (
              <Tab
                key={session.slide_id}
                active={index === activeTabIndex}
                themeColor={themeColor}
                onClick={() => onTabSwitch(index)}
              >
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__discussion')} {session.order_no}
              </Tab>
            ))}
          </TabsContainer>
        )}

        {/* Content Area */}
        <ContentArea>
          {/* Show message if no discussion sessions exist */}
          {discussionSessions.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <ChatBubbleIcon />
              </EmptyStateIcon>
              <EmptyStateText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_discussion_sessions')}</EmptyStateText>
              <EmptyStateSubtext>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__start_discussion_activity')}!
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            <TabContent>
              {/* Slide Title + prompt (SlideContent) — comes through as HTML
                  from the slide data, hence dangerouslySetInnerHTML rather
                  than rendering it as plain text. */}
              {activeSession && (
                <>
                  <SlideTitle>{activeSession.slide_title}</SlideTitle>
                  {activeSession.slide_content && (
                    <SlideDescription dangerouslySetInnerHTML={{ __html: activeSession.slide_content }} />
                  )}
                </>
              )}

              {/* Messages Area */}
              <MessagesContainer>
          {loading && messages.length === 0 ? (
            <LoadingContainer>
              <LoadingSpinner themeColor={themeColor} />
              {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__loading_messages')}...
            </LoadingContainer>
          ) : error && messages.length === 0 ? (
            <ErrorContainer>
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          ) : messages.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <ChatBubbleIcon />
              </EmptyStateIcon>
              <EmptyStateText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_messages_yet')}</EmptyStateText>
              <EmptyStateSubtext>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__be_first_to_start')}!
              </EmptyStateSubtext>
            </EmptyState>
          ) : (
            <>
              {messages.map((message) => {
                const isCurrentUser = isCurrentUserMessage(message);
                const isEditing = editingMessageId === message.id;
                
                return (
                  <MessageItem key={message.id} isCurrentUser={isCurrentUser}>
                    <MessageHeader isCurrentUser={isCurrentUser}>
                      <UserAvatar themeColor={themeColor} isCurrentUser={isCurrentUser}>
                        {getUserInitials(message.user.name)}
                      </UserAvatar>
                      <UserName isCurrentUser={isCurrentUser} themeColor={themeColor}>
                        {message.user.name}
                        {isCurrentUser && <span style={{ fontWeight: 'normal', opacity: 0.7 }}> ({GET_APP_LOCALIZE_TEXT(pageLc, 'ct__you')})</span>}
                      </UserName>
                      <MessageTime>
                        {formatMessageTime(message.timestamp)}
                        {message.edited && <EditedIndicator>({GET_APP_LOCALIZE_TEXT(pageLc, 'ct__edited')})</EditedIndicator>}
                      </MessageTime>
                    </MessageHeader>
                    
                    {isEditing ? (
                      <EditMessageContent isCurrentUser={isCurrentUser} themeColor={themeColor}>
                        <EditInput
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyPress={handleEditKeyPress}
                          themeColor={themeColor}
                          autoFocus
                        />
                        <EditActions>
                          <EditButton 
                            className="cancel" 
                            onClick={cancelEdit}
                            disabled={updating}
                          >
                            {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__cancel')}
                          </EditButton>
                          <EditButton 
                            className="save" 
                            onClick={saveEdit}
                            disabled={!editText.trim() || updating}
                            themeColor={themeColor}
                          >
                            {updating ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__saving') + '...' : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__save')}
                          </EditButton>
                        </EditActions>
                      </EditMessageContent>
                    ) : (
                        <>
                      <MessageContent isCurrentUser={isCurrentUser} themeColor={themeColor}>
                        {message.message}
                      </MessageContent>
                        {isCurrentUser && (
                          <MessageActions className="message-actions" isCurrentUser={isCurrentUser}>
                        <ActionButton
                            className="edit"
                            onClick={() => startEdit(message)}
                            title={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__edit_message')}
                        >
                          <EditIcon />
                        </ActionButton>
                        <ActionButton
                            className="delete"
                            onClick={() => showDeleteConfirmation(message)}
                            title={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__delete_message')}
                        >
                          <DeleteIcon />
                        </ActionButton>
                      </MessageActions>
                       )}
                      </>
                    )}
                  </MessageItem>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </MessagesContainer>

              {/* Input Area */}
              <InputArea>
                {error && messages.length > 0 && (
                  <ErrorContainer style={{ padding: '8px 0' }}>
                    <ErrorText>{error}</ErrorText>
                  </ErrorContainer>
                )}
                
                <InputContainer>
                  <MessageInput
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => onMessageChange(e.target.value)}
                    onKeyPress={onKeyPress}
                    placeholder={GET_APP_LOCALIZE_TEXT(pageLc, 'ct__type_message_placeholder')}
                    disabled={posting}
                    rows={1}
                    themeColor={themeColor}
                    style={{
                      borderColor: isOverLimit ? '#dc2626' : undefined
                    }}
                  />
                  <SendButton
                    onClick={onSendMessage}
                    disabled={!canSend}
                    themeColor={themeColor}
                    title={posting ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__sending') + '...' : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__send_message')}
                  >
                    {posting ? (
                      <LoadingSpinner themeColor={themeColor} style={{ width: '16px', height: '16px', margin: 0 }} />
                    ) : (
                      <SendIcon />
                    )}
                  </SendButton>
                </InputContainer>
                
                <CharacterCounter isOverLimit={isOverLimit}>
                  {characterCount}/{maxLength}
                </CharacterCounter>
              </InputArea>
            </TabContent>
          )}
        </ContentArea>
      </ModalContainer>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <ConfirmationOverlay onClick={(e) => {
          if (e.target === e.currentTarget) {
            cancelDelete();
          }
        }}>
          <ConfirmationModal>
            <ConfirmationHeader>
              <ConfirmationIcon>
                <WarningIcon />
              </ConfirmationIcon>
              <ConfirmationTitle>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__delete_message')}</ConfirmationTitle>
              <ConfirmationMessage>
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__confirm_delete_message')}
              </ConfirmationMessage>
            </ConfirmationHeader>
            <ConfirmationActions>
              <ConfirmationButton 
                className="cancel" 
                onClick={cancelDelete}
                disabled={deleting}
              >
                {GET_APP_LOCALIZE_TEXT(pageLc, 'ct__cancel')}
              </ConfirmationButton>
              <ConfirmationButton 
                className="delete" 
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? GET_APP_LOCALIZE_TEXT(pageLc, 'ct__deleting') + '...' : GET_APP_LOCALIZE_TEXT(pageLc, 'ct__delete')}
              </ConfirmationButton>
            </ConfirmationActions>
          </ConfirmationModal>
        </ConfirmationOverlay>
      )}
    </ModalOverlay>
  );
};

export default DiscussionModal;
